import React, { useEffect, useState } from 'react';
import { X, Command } from 'lucide-react';
import { getDemoSpeed, setDemoSpeed, DEMO_SPEED_OPTIONS } from '../../hooks/demoSpeed';

/* ─── Page-specific keyboard shortcut map ───
   Each key is an `activeNav` value, each value is a list of shortcut groups. */
const SHORTCUTS = {
  home: {
    title: 'Home — CEO Briefing',
    groups: [
      {
        label: 'Ad-hoc chat scenarios',
        items: [
          { keys: ['A'], desc: 'Board Meeting Prep (deck artifact flow)' },
          { keys: ['B'], desc: 'Engineering Capacity (task cards + calendar invite)' },
          { keys: ['C'], desc: 'Q2 Pipeline (content sprint)' },
        ],
      },
    ],
  },
  'mkt-campaigns': {
    title: 'Marketing — Campaigns (Priya)',
    groups: [
      {
        label: 'Ad-hoc chat scenarios',
        items: [
          { keys: ['A'], desc: 'Portfolio review — kill/boost/launch decisions (Marsi + Dora + Finn)' },
        ],
      },
      {
        label: 'Campaign actions',
        items: [
          { keys: ['click', '+ New Campaign'], desc: 'Full creation flow (Marsi + Wright + Buzz + Finn)' },
          { keys: ['click', 'Deep Dive'], desc: 'Multi-agent diagnosis per campaign (7 scenarios)' },
        ],
      },
    ],
  },
  'mkt-social': {
    title: 'Marketing — Social (Priya)',
    groups: [
      {
        label: 'Ad-hoc chat scenarios',
        items: [
          { keys: ['A'], desc: 'Channel portfolio review — what to double down on (Buzz + Dora + Marsi)' },
          { keys: ['B'], desc: 'AgentHub viral post response — 24h blitz (Dora + Buzz + Wright)' },
        ],
      },
      {
        label: 'Post actions',
        items: [
          { keys: ['click', '+ New Post'], desc: 'Full post creation flow (Buzz + Wright + Dora)' },
          { keys: ['click', 'Details'], desc: 'Post detail drawer — caption, media, hashtags, thinking trail' },
          { keys: ['click', 'Deep Dive'], desc: 'Auto-played per-post deep dive (Buzz + platform guest)' },
        ],
      },
      {
        label: 'Channel actions',
        items: [
          { keys: ['click', 'Deep Dive'], desc: '20+ event channel strategy DD on each platform card — content & social-led (Buzz + platform guest)' },
          { keys: ['click', 'View Report'], desc: 'Jumps to Analytics with channel pre-filtered' },
        ],
      },
    ],
  },
  'mkt-content': {
    title: 'Marketing — Content (Priya)',
    groups: [
      {
        label: 'Ad-hoc chat scenarios',
        items: [
          { keys: ['A'], desc: 'Content portfolio review — what\'s compounding, what\'s bleeding (Wright + Dora + Buzz + Marsi)' },
        ],
      },
      {
        label: 'Content actions',
        items: [
          { keys: ['click', '+ New Copy'], desc: 'Full content creation flow (Wright + Dora + Buzz); delivered to Review column' },
          { keys: ['click', 'Details'], desc: 'Content detail drawer — hero preview, SEO, keywords, thinking trail' },
          { keys: ['click', 'Deep Dive'], desc: 'Auto-played per-item deep dive — 3 hero pieces get multi-agent scripts' },
        ],
      },
    ],
  },
  'mkt-analytics': {
    title: 'Marketing — Analytics (Priya)',
    groups: [
      {
        label: 'Ad-hoc chat scenarios',
        items: [
          { keys: ['A'], desc: 'Cross-channel reallocation review — 3 HITL decisions (Marsi + Finn + Dora + Buzz)' },
        ],
      },
      {
        label: 'Analytics actions',
        items: [
          { keys: ['click', 'Deep Dive'], desc: 'Auto-played per-channel deep dive — Meta gets deepest (alert channel)' },
        ],
      },
    ],
  },
  'sales-deals': {
    title: 'Sales — Deals (David)',
    groups: [
      {
        label: 'Ad-hoc chat scenarios',
        items: [
          { keys: ['A'], desc: 'Pipeline health review — 3 HITL decisions (Sally + Luc + Stark + Finn, week execution plan)' },
        ],
      },
      {
        label: 'Deal actions',
        items: [
          { keys: ['click', '+ New Deal'], desc: 'Full new-deal research flow (Sally + Dora + Stark); lands in Prospecting' },
          { keys: ['click', 'Deep Dive'], desc: 'Stage-aware per-deal deep dive (Sally-led, multi-agent) with HITL approvals + mock PDF/email deliverables' },
          { keys: ['click', 'Insight Deep Dive'], desc: 'Auto-played drill-down on each Sally insight card' },
        ],
      },
    ],
  },
  'sales-pipeline': {
    title: 'Sales — Pipeline (David)',
    groups: [
      {
        label: 'Ad-hoc chat scenarios',
        items: [
          { keys: ['A'], desc: 'Quarter pacing review — Sally + Finn + Stark + Luc (time reallocation). 3 HITL decisions.' },
        ],
      },
      {
        label: 'Pipeline actions',
        items: [
          { keys: ['click', 'Deep Dive (top-right)'], desc: 'Full pipeline deep dive — Sally + Dora + Stark + Finn. Delivers Q1 Pipeline Health Report PDF + email.' },
          { keys: ['click', 'Forecast Deep Dive'], desc: 'Forecast-vs-target drill-down — Finn + Sally + Stark' },
          { keys: ['click', 'Loss Reasons Deep Dive'], desc: 'Loss pattern analysis — Dora + Sally + Finn + Stark' },
        ],
      },
    ],
  },
  'sales-outreach': {
    title: 'Sales — Outreach (David)',
    groups: [
      {
        label: 'Ad-hoc chat scenarios',
        items: [
          { keys: ['A'], desc: 'Outreach calibration review — Sally + Wright + Dora + Marsi. 3 HITL decisions.' },
        ],
      },
      {
        label: 'Outreach actions',
        items: [
          { keys: ['click', 'Deep Dive (top-right)'], desc: 'Full portfolio deep dive — Sally + Wright + Dora + Marsi. Delivers Outreach Weekly Digest PDF + email.' },
          { keys: ['click', '+ New Sequence'], desc: 'Sequence creation flow (Sally + Wright + Marsi + Dora). Final HITL: publish / save as draft / schedule.' },
          { keys: ['click', 'Deep Dive (per sequence)'], desc: 'Stage-aware sequence deep dive — fix/scale/optimize/assess/decide narratives' },
        ],
      },
    ],
  },
  'sales-reports': {
    title: 'Sales — Reports (David)',
    groups: [
      {
        label: 'Ad-hoc chat scenarios',
        items: [
          { keys: ['A'], desc: 'Weekly reports calibration — Sally + Wright + Dora + Finn. 3 HITL decisions.' },
        ],
      },
      {
        label: 'Reports actions',
        items: [
          { keys: ['click', 'Download'], desc: 'Downloads Q1 Sales Report PDF — animated spinner → check → toast confirmation.' },
          { keys: ['click', 'Deep Dive (top-right)'], desc: 'Full Q1 read — Sally + Wright + Dora + Finn + Stark. Delivers Q1 Sales Report PDF + email to David & Arjun.' },
          { keys: ['click', 'Deep Dive (Monthly Revenue)'], desc: 'Momentum drill — Sally + Finn + Dora + Marsi. Velocity vs streak read.' },
          { keys: ['click', 'Deep Dive (Win / Loss)'], desc: 'Loss-reason breakdown — Sally + Dora + Wright + Stark. Pricing-tier escalation memo.' },
          { keys: ['click', 'Deep Dive (Rep Performance)'], desc: 'Rep-by-rep read — Sally + Wright + Marsi + Dora. Sarah routing + Mike template plan.' },
          { keys: ['click', 'Deep Dive (per insight)'], desc: '4 insight-specific dives: demo SLA · proposal stall · ROI deck leverage · momentum playbook.' },
        ],
      },
    ],
  },
  'home-marketing': {
    title: 'Home — Marketing Lead (Priya)',
    groups: [
      {
        label: 'Ad-hoc chat scenarios',
        items: [
          { keys: ['A'], desc: 'Implify Teams launch play (Marsi + Wright + Buzz + Dora)' },
          { keys: ['B'], desc: 'AgentHub counter-positioning — 72h response (Dora + Marsi + Wright + Buzz)' },
        ],
      },
    ],
  },
  'home-sales': {
    title: 'Home — Sales Director (David)',
    groups: [
      {
        label: 'Ad-hoc chat scenarios',
        items: [
          { keys: ['A'], desc: 'Implify Teams sales readiness (Sally + Dora + Finn + Luc)' },
          { keys: ['B'], desc: 'Friday board contribution — sales story (Sally + Finn + Luc)' },
        ],
      },
    ],
  },
  'org-pulse': {
    title: 'Org Pulse',
    groups: [
      {
        label: 'Strategic scenarios',
        items: [
          { keys: ['A'], desc: 'New Product Line brainstorm (Stark → Dora → Finn)' },
          { keys: ['B'], desc: 'Competitor Threat & Strategic Shift (SWOT)' },
        ],
      },
    ],
  },
  marketing: {
    title: 'Marketing Department',
    groups: [
      {
        label: 'Scenarios (multi-agent)',
        items: [
          { keys: ['A'], desc: 'Marketing trends analysis — where do we stack up? (Marsi + Dora + Buzz)' },
          { keys: ['B'], desc: 'New product GTM brainstorm — Implify Teams launch (5-agent showcase)' },
        ],
      },
    ],
  },
  sales: {
    title: 'Sales Department',
    groups: [
      {
        label: 'Scenarios (multi-agent)',
        items: [
          { keys: ['A'], desc: 'New product line sales readiness (Sally → Stark → Dora)' },
          { keys: ['B'], desc: 'Sales cycle velocity — compressing 34 days (Sally + Finn)' },
        ],
      },
    ],
  },
  support: {
    title: 'Support Department',
    groups: [
      {
        label: 'Scenarios (multi-agent)',
        items: [
          { keys: ['A'], desc: 'New product line support readiness (Zira → Stark → Tali)' },
          { keys: ['B'], desc: 'Customer churn pattern deep dive (Zira + Finn)' },
        ],
      },
    ],
  },
  finance: {
    title: 'Finance Department',
    groups: [
      {
        label: 'Scenarios (multi-agent)',
        items: [
          { keys: ['A'], desc: 'Series B readiness — honest read (Finn → Stark → Sally → Tali)' },
          { keys: ['B'], desc: 'CAC & unit economics deep dive (Finn + Marsi)' },
        ],
      },
    ],
  },
  people: {
    title: 'People Department',
    groups: [
      {
        label: 'Scenarios (multi-agent)',
        items: [
          { keys: ['A'], desc: 'L&D gap vs industry (Tali → Dora → Finn)' },
          { keys: ['B'], desc: 'AI impact on team — proactive plan (Tali + Finn)' },
        ],
      },
    ],
  },
};

/* ─── Always-on global shortcuts ─── */
const GLOBAL_SHORTCUTS = [
  { keys: ['?'], desc: 'Toggle this cheat sheet' },
  { keys: ['Esc'], desc: 'Close open panels & modals' },
];

const V4CheatSheet = ({ isDark, open, onClose, activeNav, activeUserId }) => {
  const [speed, setSpeed] = useState(getDemoSpeed());
  useEffect(() => {
    if (!open) return;
    setSpeed(getDemoSpeed());
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  // Normalize nav → look up page shortcuts. Dept sub-pages share the root map, except mkt-campaigns which has its own.
  let pageKey = activeNav;
  if (activeNav === 'home' && activeUserId === 'marketing') pageKey = 'home-marketing';
  else if (activeNav === 'home' && activeUserId === 'sales') pageKey = 'home-sales';
  else if (activeNav === 'mkt-campaigns') pageKey = 'mkt-campaigns';
  else if (activeNav === 'mkt-social') pageKey = 'mkt-social';
  else if (activeNav === 'mkt-content') pageKey = 'mkt-content';
  else if (activeNav === 'mkt-analytics') pageKey = 'mkt-analytics';
  else if (activeNav?.startsWith('mkt')) pageKey = 'marketing';
  else if (activeNav === 'sales-deals') pageKey = 'sales-deals';
  else if (activeNav === 'sales-pipeline') pageKey = 'sales-pipeline';
  else if (activeNav === 'sales-outreach') pageKey = 'sales-outreach';
  else if (activeNav === 'sales-reports') pageKey = 'sales-reports';
  else if (activeNav?.startsWith('sales-')) pageKey = 'sales';
  else if (activeNav?.startsWith('support-')) pageKey = 'support';
  else if (activeNav?.startsWith('finance-')) pageKey = 'finance';
  else if (activeNav?.startsWith('people-')) pageKey = 'people';
  const pageConfig = SHORTCUTS[pageKey];

  const solidBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const border = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const sectionBg = isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]';
  const keyBg = isDark ? 'bg-white/[0.08] text-white border-white/[0.08]' : 'bg-white text-slate-700 border-black/[0.08]';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ background: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.25)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="cheat-sheet-overlay"
    >
      <div
        className={`${solidBg} border ${border} rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden`}
        style={{ animation: 'cheatIn 0.2s ease-out' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="cheat-sheet-modal"
      >
        <style>{`@keyframes cheatIn { from { opacity: 0; transform: scale(0.96) translateY(4px); } to { opacity: 1; transform: scale(1) translateY(0); } }`}</style>

        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${border}`}>
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isDark ? 'bg-indigo-500/15 text-indigo-400' : 'bg-indigo-500/10 text-indigo-600'}`}>
              <Command className="w-3.5 h-3.5" />
            </div>
            <div>
              <p className={`text-sm font-semibold ${textPrimary}`}>Keyboard Shortcuts</p>
              <p className={`text-[11px] ${textSecondary}`}>Press <kbd className={`px-1.5 py-0.5 rounded border text-[10px] ${keyBg}`}>?</kbd> anytime to toggle</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-white/[0.06] text-white/50' : 'hover:bg-black/[0.04] text-slate-500'}`}
            data-testid="cheat-sheet-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 max-h-[85vh] overflow-y-auto">
          {pageConfig ? (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-[11px] font-semibold uppercase tracking-wider ${textSecondary}`}>{pageConfig.title}</span>
                <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'}`} />
              </div>
              {pageConfig.groups.map((group, gi) => (
                <div key={gi} className={`${sectionBg} rounded-lg p-3.5 mb-2.5 border ${border}`}>
                  <p className={`text-[11px] font-medium mb-2.5 ${textSecondary}`}>{group.label}</p>
                  <ul className="space-y-2">
                    {group.items.map((it, i) => (
                      <li key={i} className="flex items-center justify-between gap-4">
                        <span className={`text-[13px] ${textPrimary}`}>{it.desc}</span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {it.keys.map((k, ki) => (
                            <kbd key={ki} className={`min-w-[26px] text-center px-2 py-1 rounded-md text-[11px] font-semibold border ${keyBg}`}>
                              {k}
                            </kbd>
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          ) : (
            <div className={`text-[13px] ${textSecondary} text-center py-6`}>
              No page-specific shortcuts here yet. Try Home, Org Pulse, or Marketing.
            </div>
          )}

          {/* Global shortcuts */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-[11px] font-semibold uppercase tracking-wider ${textSecondary}`}>Global</span>
              <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'}`} />
            </div>
            <div className={`${sectionBg} rounded-lg p-3.5 border ${border}`}>
              <ul className="space-y-2">
                {GLOBAL_SHORTCUTS.map((it, i) => (
                  <li key={i} className="flex items-center justify-between gap-4">
                    <span className={`text-[13px] ${textPrimary}`}>{it.desc}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {it.keys.map((k, ki) => (
                        <kbd key={ki} className={`min-w-[26px] text-center px-2 py-1 rounded-md text-[11px] font-semibold border ${keyBg}`}>
                          {k}
                        </kbd>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Demo speed */}
          <section data-testid="cheat-sheet-demo-speed">
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-[11px] font-semibold uppercase tracking-wider ${textSecondary}`}>Demo speed</span>
              <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'}`} />
            </div>
            <div className={`${sectionBg} rounded-lg p-3.5 border ${border} flex items-center justify-between gap-4`}>
              <div>
                <p className={`text-[13px] font-medium ${textPrimary}`}>Speed up scenario playback</p>
                <p className={`text-[11px] mt-0.5 ${textSecondary}`}>Scales every multi-agent timer in real time. New scenarios use the new speed.</p>
              </div>
              <div className={`flex items-center rounded-lg p-0.5 border ${border} ${isDark ? 'bg-black/20' : 'bg-white/60'}`}>
                {DEMO_SPEED_OPTIONS.map((opt) => {
                  const isActive = speed === opt;
                  return (
                    <button
                      key={opt}
                      onClick={() => { setDemoSpeed(opt); setSpeed(opt); }}
                      className={`px-3 py-1 rounded-md text-[12px] font-semibold transition-colors ${
                        isActive
                          ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white'
                          : (isDark ? 'text-white/55 hover:text-white' : 'text-slate-500 hover:text-slate-800')
                      }`}
                      data-testid={`cheat-sheet-speed-${opt}`}
                    >
                      {opt}×
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Replay onboarding tour */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-[11px] font-semibold uppercase tracking-wider ${textSecondary}`}>Onboarding</span>
              <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'}`} />
            </div>
            <div className={`${sectionBg} rounded-lg p-3.5 border ${border} flex items-center justify-between gap-4`}>
              <div>
                <p className={`text-[13px] font-medium ${textPrimary}`}>Replay the welcome tour</p>
                <p className={`text-[11px] mt-0.5 ${textSecondary}`}>Re-run the 60-second walkthrough of what's interactive</p>
              </div>
              <button
                onClick={() => { onClose?.(); window.dispatchEvent(new CustomEvent('implify:open-tour')); }}
                className={`flex-shrink-0 px-3.5 py-1.5 rounded-lg text-[12px] font-semibold transition-colors bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:from-indigo-400 hover:to-purple-500`}
                data-testid="cheat-sheet-replay-tour"
              >
                Replay tour
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default V4CheatSheet;
